import { ArrowRight, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../configs/api";
import { formatPrice, openInNewTab } from "../../../utils/helpers";
import ComingSoonCard from "../userCommon/ComingSoonCard";
import { TbBuildingSkyscraper } from "react-icons/tb";

const HomeProjectCard = ({ project }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200  shadow-sm hover:shadow-lg transition-all duration-200 transform-gpu">
      {/* Image */}
      <div className="relative overflow-hidden bg-gray-100 rounded-t-xl">
        <img
          src={project?.thumbnail}
          alt={project.title || "Project"}
          className="w-full h-48 sm:h-60 object-cover transition-transform transform-gpu duration-300 hover:scale-105"
        />
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Top section: Logo + Title */}
        <div className="flex items-start gap-1.5 mb-2 ">
          <div className="min-h-10 flex items-center justify-center">
            {project?.companyLogo ? (
              <img
                src={project?.companyLogo}
                alt="Logo"
                loading="lazy"
                className="w-10 h-10  object-cover rounded-lg"
              />
            ) : (
              <TbBuildingSkyscraper className="text-gray-400 size-8" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-[#031722] text-[0.9rem] tracking-wide line-clamp-1 ">
              {project.title}
            </h3>
            {project?.companyName && (
              <p className="text-[#6A6A6A] text-[0.7rem] line-clamp-1">
                By {project?.companyName}
              </p>
            )}
          </div>
        </div>

        {/* Price */}
        <p className="font-medium text-[#031722] text-[0.9rem] my-2 ">
          {project?.price ? formatPrice(project?.price) : "Price on Request"}
        </p>

        {/* Location */}
        <div className="flex items-center gap-2 mb-2 text-[#6A6A6A] text-[0.8rem]">
          <MapPin className="w-4 h-4 flex-shrink-0 text-gray-500" />
          <p className="truncate">{project?.location}</p>
        </div>

        {/* Button */}
        <button
          onClick={() => openInNewTab(`/properties/project/${project._id}`)}
          className="w-full py-2.5 border border-public-highlight text-public-highlight rounded-3xl font-medium text-[0.85rem] transition-all duration-300 hover:bg-public-highlight hover:text-white"
        >
          View Details
        </button>
      </div>
    </div>
  );
};

const SkeletonCard = () => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Image Skeleton */}
      <div className="h-48 sm:h-60 bg-gray-200 animate-pulse rounded-t-xl"></div>

      {/* Content */}
      <div className="p-3 lg:p-3">
        {/* Logo + Title Row */}
        <div className="flex items-start gap-1.5 mb-2">
          {/* Logo box */}
          <div className="w-10 h-10 bg-gray-300 rounded-lg animate-pulse"></div>

          {/* Title + Builder */}
          <div className="flex-1 min-w-0 space-y-1">
            <div className="h-4 bg-gray-300 rounded w-3/4 animate-pulse"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
          </div>
        </div>

        {/* Price */}
        <div className="h-4 bg-gray-300 rounded w-2/5 my-2 animate-pulse"></div>

        {/* Location */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-4 h-4 bg-gray-300 rounded animate-pulse"></div>
          <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse"></div>
        </div>

        {/* Button */}
        <div className="w-full h-10 bg-gray-300 rounded-3xl animate-pulse"></div>
      </div>
    </div>
  );
};

const FeaturedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const cardCount = 5;

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const res = await api.get(
          `/properties/home?type=project&count=${cardCount}`
        );
        const data = Array.isArray(res.data.data) ? res.data.data : [];
        setProjects(data.slice(0, cardCount)); // MAX 5
      } catch (err) {
        console.error("Error loading:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleViewAll = () => {
    navigate("/properties/project");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 bg-white pb-6 md:pb-8 lg:pb-10">
      <div className="mx-auto">
        {/* HEADER */}
        <div className="flex justify-between items-center pb-6 md:pb-8 lg:pb-10">
          <h2 className="font-medium text-[#031722] text-lg sm:text-xl md:text-2xl">
            Featured Projects
          </h2>

          <button
            onClick={handleViewAll}
            className="flex items-center gap-1 sm:gap-2 text-red-600 font-medium text-xs sm:text-base"
          >
            View all <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
          </button>
        </div>

        {/* SCROLLABLE GRID */}
        <div className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory xl:grid xl:grid-cols-5 xl:overflow-x-visible xl:snap-none">
          {/* LOADING SKELETONS */}
          {loading &&
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={"skeleton-" + i}
                className="snap-start shrink-0 w-[75%] sm:w-[50%] md:w-[33%] xl:w-auto xl:max-w-sm"
              >
                <SkeletonCard />
              </div>
            ))}

          {/* PROJECT CARDS */}
          {!loading &&
            projects.map((project, i) => (
              <div
                key={project._id}
                className="snap-start shrink-0 w-[75%] sm:w-[50%] md:w-[33%] xl:w-auto xl:max-w-sm"
              >
                <HomeProjectCard project={project} />
              </div>
            ))}

          {/* COMING SOON (Fillers) */}
          {!loading &&
            projects.length < 5 &&
            Array.from({ length: 5 - projects.length }).map((_, i) => (
              <ComingSoonCard />
            ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedProjects;

// import { useState, useEffect } from "react";
// import { ArrowRight, MapPin, Clock } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import api from "../../../configs/api";
// import FeaturedProjectsLogo from "../../../assets-user/FeaturedProjectsLogo.png";
// import "aos/dist/aos.css";
// import { formatPrice } from "../../../utils/helpers";

// const FeaturedProjects = () => {
//   const [projects, setProjects] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const navigate = useNavigate();

//   // Fetch featured projects
//   useEffect(() => {
//     const fetchFeaturedProjects = async () => {
//       try {
//         setLoading(true);
//         const response = await api.get("/properties?type=project");
//         // console.log("Data===>", response.data);
//         const allProjects = Array.isArray(response.data.data)
//           ? response.data.data
//           : [];

//         // Limit to 4 projects
//         const limited = allProjects.slice(0, 4);
//         setProjects(limited);
//       } catch (error) {
//         console.error("Error fetching featured projects:", error);
//         setProjects([]);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchFeaturedProjects();
//   }, []);

//   // Coming Soon
//   const getComingSoonCount = () => {
//     if (loading) return 0;
//     const projectCount = projects.length;
//     if (projectCount === 1) return 3;
//     if (projectCount === 2) return 2;
//     if (projectCount === 3) return 1;
//     return 0;
//   };

//   const comingSoonCount = getComingSoonCount();

//   // Render Coming Soon Card
//   const renderComingSoonCard = (index) => (
//     <div
//       key={`coming-soon-${index}`}
//       data-aos="fade-up"
//       data-aos-delay={`${(projects.length + index) * 150}`}
//       className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden shadow-md flex-shrink-0 w-[80%] md:w-[70%] lg:w-auto snap-start border-2 border-dashed border-gray-300 mb-4"
//     >
//       {/* Image Section with Coming Soon Message */}
//       <div className="relative h-48 sm:h-52 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
//         <div className="text-center">
//           <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3 animate-pulse" />
//           <p className="text-gray-600 font-semibold text-lg">Coming Soon</p>
//           <p className="text-gray-500 text-sm mt-1">New projects arriving</p>
//         </div>
//       </div>

//       {/* Project Info Placeholder */}
//       <div className="p-4">
//         <div className="flex items-start gap-3 mb-2">
//           <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-300 rounded-lg animate-pulse flex-shrink-0"></div>
//           <div className="flex-1 min-w-0 space-y-2">
//             <div className="h-5 bg-gray-300 rounded w-3/4 animate-pulse"></div>
//             <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
//           </div>
//         </div>

//         {/* Price Placeholder */}
//         <div className="h-5 bg-gray-300 rounded w-2/5 mb-2 animate-pulse"></div>

//         {/* Location Placeholder */}
//         <div className="flex items-center gap-2 mb-2">
//           <div className="w-4 h-4 bg-gray-300 rounded animate-pulse"></div>
//           <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
//         </div>

//         {/* Button Placeholder */}
//         <div className="w-full h-10 sm:h-12 bg-gray-300 rounded-3xl animate-pulse"></div>
//       </div>
//     </div>
//   );

//   const handleViewDetails = (id) => {
//     navigate(`/properties/project/${id}`);
//     window.scrollTo({ top: 0, behavior: "smooth" });
//   };

//   return (
//     <div
//       className="w-full px-4 sm:px-6 md:px-8 lg:px-10 pb-4 md:pb-6 lg:pb-8 bg-white"
//       data-aos="fade-up"
//     >
//       <div className="mx-auto">
//         <div className="flex justify-between items-center pb-4 md:pb-6 lg:pb-8">
//           <h2 className="font-medium text-[#031722] text-lg sm:text-xl md:text-2xl ">
//             Featured Projects{" "}
//           </h2>

//           <button
//             onClick={() => {
//               navigate("/properties/project");
//               window.scrollTo(0, 0);
//             }}
//             className="flex items-center gap-1 sm:gap-2 text-red-600 font-medium text-xs sm:text-base "
//           >
//             View all <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
//           </button>
//         </div>

//         {/* 🔹 Projects Grid */}
//         <div
//           className="flex overflow-x-auto lg:overflow-hidden gap-4 md:gap-6 lg:grid lg:grid-cols-4 scroll-smooth snap-x snap-mandatory"
//           data-aos="fade-up"
//           data-aos-delay="300"
//         >
//           {loading
//             ? // 🔸 Skeleton Loader
//               Array(4)
//                 .fill(0)
//                 .map((_, i) => (
//                   <div
//                     key={i}
//                     className="bg-white rounded-lg overflow-hidden shadow-md flex-shrink-0 w-[80%] md:w-[70%] lg:w-auto snap-start"
//                   >
//                     <div className="h-48 sm:h-52 bg-gray-200"></div>
//                     <div className="p-4 space-y-3">
//                       <div className="h-5 bg-gray-200 rounded w-2/3"></div>
//                       <div className="h-4 bg-gray-200 rounded w-1/3"></div>
//                       <div className="h-4 bg-gray-200 rounded w-1/2"></div>
//                     </div>
//                   </div>
//                 ))
//             : // 🔹 Project Cards
//               projects.map((project, index) => {
//                 const thumbnail =
//                   project.images?.find((img) => img.isThumbnail)?.url ||
//                   project.images?.[0]?.url ||
//                   FeaturedProjectsLogo;

//                 return (
//                   <div
//                     key={project._id}
//                     onClick={() => handleViewDetails(project._id)}
//                     className="bg-white rounded-lg overflow-hidden shadow-md cursor-pointer flex-shrink-0 w-[80%] md:w-[70%] lg:w-auto snap-start hover:shadow-lg hover:scale-[1.02] transition-all duration-300 mb-4 border-2"
//                     data-aos="fade-up"
//                     data-aos-delay={`${index * 150}`}
//                   >
//                     {/* 🖼 Project Image */}
//                     <div className="relative overflow-hidden bg-gray-100">
//                       <img
//                         src={thumbnail}
//                         alt={project.title || "Project"}
//                         className="w-full h-48 sm:h-52 object-cover"
//                       />
//                     </div>

//                     {/* 🏗 Project Info */}
//                     <div className="p-4">
//                       <div className="flex items-start gap-3 mb-2">
//                         <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
//                           <img
//                             src={FeaturedProjectsLogo}
//                             alt="Logo"
//                             className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
//                           />
//                         </div>

//                         <div className="flex-1 min-w-0">
//                           <h3 className="font-medium text-[#031722] text-base sm:text-lg md:text-xl lg:text-[18.7px] truncate">
//                             {project.title || "Untitled Project"}
//                           </h3>
//                           <p className="font-normal text-[#6A6A6A] text-sm sm:text-base truncate">
//                             By{" "}
//                             {project.property?.builderName || "Unknown Builder"}
//                           </p>
//                         </div>
//                       </div>

//                       {/* 💰 Price */}
//                       <p className="font-medium text-[#031722] text-base sm:text-lg md:text-xl lg:text-[18.7px] mb-2">
//                         {project?.property?.price
//                           ? formatPrice(project.property.price)
//                           : "Price on Request"}
//                       </p>

//                       {/* 📍 Location */}
//                       <div className="flex items-center gap-2 mb-2 text-gray-600">
//                         <MapPin className="w-4 h-4 flex-shrink-0" />
//                         <p className="font-normal text-[#6A6A6A] text-sm sm:text-base truncate">
//                           {project.location?.address?.formatted || "Odisha"}
//                         </p>
//                       </div>

//                       {/* 🔘 Button */}
//                       <button
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           handleViewDetails(project._id);
//                         }}
//                         className="w-full py-2.5 sm:py-3 border-2 border-red-600 text-red-600 rounded-3xl font-medium text-sm sm:text-base transition-all duration-300 hover:bg-red-600 hover:text-white"
//                       >
//                         View Details
//                       </button>
//                     </div>
//                   </div>
//                 );
//               })}

//           {/* Render Coming Soon cards */}
//           {!loading &&
//             Array.from({ length: comingSoonCount }).map((_, index) =>
//               renderComingSoonCard(index)
//             )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default FeaturedProjects;
